import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { delay } from '@sequencehq/utils'
import { SECONDS } from '@sequencehq/utils/dates'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

const POLL_INTERVAL = 500
const MAX_WAIT_FOR_TASK = 3 * SECONDS
const MAX_WAIT_FOR_COMPLETION = 10 * SECONDS

type ScheduleCreationResult = {
  billingScheduleId: string | undefined
}

/**
 * After completing a contract review task, polls the automation execution
 * to check for a create_schedule task. If one appears, waits for it to
 * complete and returns the billingScheduleId.
 */
export const waitForScheduleCreation = async (
  automationExecutionId: string
): Promise<ScheduleCreationResult> => {
  const createScheduleTask = await pollForCreateScheduleTask(
    automationExecutionId,
    MAX_WAIT_FOR_TASK
  )

  if (!createScheduleTask) {
    return { billingScheduleId: undefined }
  }

  if (createScheduleTask.status === 'COMPLETED') {
    return {
      billingScheduleId: createScheduleTask.outputData?.billingScheduleId as
        | string
        | undefined
    }
  }

  return pollForTaskCompletion(automationExecutionId, MAX_WAIT_FOR_COMPLETION)
}

const pollForCreateScheduleTask = async (
  executionId: string,
  timeout: number
) => {
  const start = Date.now()

  while (Date.now() - start < timeout) {
    const { data } = await dashboardv99990101Client.getAutomationExecution({
      id: executionId
    })

    const task = data?.tasks.find(
      t =>
        t.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.createSchedule
    )

    if (task) {
      return task
    }

    await delay(POLL_INTERVAL)
  }

  return undefined
}

const pollForTaskCompletion = async (
  executionId: string,
  timeout: number
): Promise<ScheduleCreationResult> => {
  const start = Date.now()

  while (Date.now() - start < timeout) {
    const { data } = await dashboardv99990101Client.getAutomationExecution({
      id: executionId
    })

    const task = data?.tasks.find(
      t =>
        t.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.createSchedule
    )

    if (!task || task.status === 'FAILED' || task.status === 'CANCELED') {
      return { billingScheduleId: undefined }
    }

    if (task.status === 'COMPLETED') {
      return {
        billingScheduleId: task.outputData?.billingScheduleId as
          | string
          | undefined
      }
    }

    await delay(POLL_INTERVAL)
  }

  return { billingScheduleId: undefined }
}
